import { useEffect, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  ClearScheduleModal,
  MarkInactiveModal,
  MiAvatar,
  MiIcon,
  MiMenu,
  RemoveInactiveModal,
} from "@miplatform/components";
import {
  AVAILABILITY_LOG_TYPE,
  COLUMN_WIDTH,
  ENTITY_TYPES,
  ICONS_WEB,
  PERMISSIONS,
} from "@miplatform/constants";
import { useBoolean, useRouter } from "@miplatform/hooks";
import { MiTHEME } from "@miplatform/theme";
import {
  getDate,
  getInitials,
  getRootUrl,
  isBefore,
  spaceWords,
  toKebabCase,
} from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, Typography } from "@mui/material";
import { useViewConfig } from "../ViewConfigContext";
import { EditCapacityModal } from "./EditCapacityModal/EditCapacityModal";
import { useUserDetailsViewModel } from "./useUserDetailsViewModel";

function Status({ icon, iconColor, status, classes }) {
  const statusText = spaceWords(status);

  return (
    <Box
      className={`${classes.statusContainer} ${classes.flexCenter}`}
      data-testid={`div-${toKebabCase(statusText)}`}
    >
      <Box className={classes.icon} data-testid="status-icon">
        <MiIcon
          size={0.8}
          path={icon || mdi[ICONS_WEB.CIRCLE_SLICE]}
          color={iconColor || MiTHEME.palette.grey.medium}
        />
      </Box>
      <Typography
        className={classes.text}
        data-testid={toKebabCase(statusText)}
      >
        {statusText}
      </Typography>
    </Box>
  );
}

const MENU_TYPES = {
  NONE: "none",
  INITIAL: "initial",
  VIEW: "view",
  STATUS: "status",
  SCHEDULING: "scheduling",
};

/**
 * UserDetails component props
 *
 * @component
 *
 * @param {Object} props - Component props.
 * @param {Object} props.user - The user object.
 * @param {string} props.user.id - The user id.
 * @param {string} props.user.location - The user name.
 * @param {string} props.user.modifiedOn - The user color.
 * @param {string} props.user.name - The user status.
 * @param {string} props.user.activityStatus - The user status.
 * @param {string} props.user.availbityStatus - The user status.
 * @param {?string} props.user.activityStatus obselete - The user status.
 *
 * @returns {JSX.Element}
 */
export function UserDetails({
  hideMenu,
  user = {},
  date,
  onClick = () => {},
  isSelected,
  setSelectedUserId = () => {},
  onSelect = null,
  onRefresh = () => {},
}) {
  const { activityStatus, name, color } = user;
  const { navigate } = useRouter();
  const { classes, theme } = useStyles({
    color,
    isSelected,
    isSelectable: !!onSelect,
  });
  const {
    activityStatuses,
    clearSchedule,
    isClearScheduleModalOpen,
    openClearScheduleModal,
    closeClearScheduleModal,
  } = useUserDetailsViewModel({
    user,
    date,
  });
  const { icon: activityIcon, color: activityIconColor } =
    activityStatuses.find((status) => status.value === activityStatus) || {};
  const [
    isEditCapacityModalOpen,
    handleEditCapacityModalOpen,
    handleEditCapacityModalClose,
  ] = useBoolean();
  const [
    isMarkInactiveModalOpen,
    handleMarkInactiveModalOpen,
    handleMarkInactiveModalClose,
  ] = useBoolean();
  const [
    isRemoveInactiveModalOpen,
    handleRemoveInactiveModalOpen,
    handleRemoveInactiveModalClose,
  ] = useBoolean();
  const [menuType, setMenuType] = useState(MENU_TYPES.NONE);

  const containerRef = useRef(null);

  const { hasPermission } = usePermissions();

  const { calendarSet } = useViewConfig();

  const showEditCapacity =
    hasPermission(PERMISSIONS.USER_DAILY_CAPACITY.UPDATE) ||
    (hasPermission(PERMISSIONS.USER_DAILY_CAPACITY.CREATE) &&
      calendarSet?.eventType === ENTITY_TYPES.ORDER);

  const hideEditCapacityMenuItem =
    menuType !== MENU_TYPES.INITIAL || !showEditCapacity;

  useEffect(() => {
    if (isSelected && containerRef.current && menuType === MENU_TYPES.NONE) {
      containerRef.current.scrollIntoView({
        behavior: "smooth",
        inline: "center",
      });
    }
  }, [isSelected, MENU_TYPES]);

  const menuData = [
    {
      label: "View",
      icon: mdi.mdiArrowRightThin,
      action: () => {
        setMenuType(MENU_TYPES.VIEW);
      },
      keepMenuOpen: true,
      hide: menuType !== MENU_TYPES.INITIAL,
    },
    {
      label: "Status",
      icon: mdi.mdiFormatListBulleted,
      action: () => {
        setMenuType(MENU_TYPES.STATUS);
      },
      keepMenuOpen: true,
      hide: menuType !== MENU_TYPES.INITIAL,
    },
    {
      label: "Scheduling",
      icon: mdi.mdiCalendarClock,
      action: () => {
        setMenuType(MENU_TYPES.SCHEDULING);
      },
      keepMenuOpen: true,
      hide: menuType !== MENU_TYPES.INITIAL,
    },
    {
      label: "Edit Capacity",
      icon: mdi.mdiPencil,
      action: handleEditCapacityModalOpen,
      hide: hideEditCapacityMenuItem,
    },
    {
      label: "View More",
      icon: mdi.mdiArrowRightThin,
      action: () => {
        onClick({ user });
      },
      hide: menuType !== MENU_TYPES.VIEW,
    },
    {
      label: "View Profile",
      icon: mdi.mdiAccountOutline,
      action: () => {
        navigate(`/profile/${user.id}`);
      },
      hide: menuType !== MENU_TYPES.VIEW,
    },
    {
      label: "Mark Inactive",
      icon: mdi.mdiClipboardAccountOutline,
      color: theme.palette.grey.medium,
      action: handleMarkInactiveModalOpen,
      hide: menuType !== MENU_TYPES.SCHEDULING || isBefore(date, getDate()),
    },
    {
      label: "Remove Inactive",
      icon: mdi.mdiClipboardAccountOutline,
      color: theme.palette.grey.medium,
      action: handleRemoveInactiveModalOpen,
      hide:
        menuType !== MENU_TYPES.SCHEDULING ||
        !user.availabilityLog?.some(
          (log) => log.type === AVAILABILITY_LOG_TYPE.INACTIVE
        ),
    },
    {
      label: "Clear Schedule",
      icon: mdi.mdiCancel,
      color: theme.palette.error.main,
      action: () => {
        openClearScheduleModal();
      },
      hide: menuType !== MENU_TYPES.SCHEDULING,
    },
    ...activityStatuses.map((status) => ({
      ...status,
      action: (_, item) => {
        status.action(item);
      },
      hide: menuType !== MENU_TYPES.STATUS,
    })),
  ];

  const onMenuOpen = () => {
    setMenuType(MENU_TYPES.INITIAL);
    setSelectedUserId(user.id);
  };

  const onMenuClose = () => {
    setMenuType(MENU_TYPES.NONE);
    setSelectedUserId("");
  };

  return (
    <Box
      ref={containerRef}
      className={classes.container}
      onClick={onSelect ? () => onSelect(user) : undefined}
    >
      <Box className={`${classes.header} ${classes.flexCenter}`}>
        <MiAvatar
          className={classes.avatar}
          data-testid="user-initials"
          src={
            user?.profilePicture
              ? `${getRootUrl()}/${user?.profilePicture.referenceId}`
              : undefined
          }
        >
          {getInitials(name)}
        </MiAvatar>
        <Typography className={classes.text}>{name}</Typography>
        {!hideMenu && (
          <MiMenu
            condensed
            showCloseDropdown
            boldText
            testID="user-details-menu"
            data={menuData}
            onMenuOpen={onMenuOpen}
            closeContextMenu={onMenuClose}
          />
        )}
      </Box>
      <Status
        icon={activityIcon}
        iconColor={activityIconColor}
        status={activityStatus}
        classes={classes}
      />
      {color && (
        <Box className={classes.borderColor} data-testid="border-color" />
      )}
      <ClearScheduleModal
        isOpen={isClearScheduleModalOpen}
        onClose={closeClearScheduleModal}
        onConfirm={clearSchedule}
        date={date}
        name={name}
      />
      <MarkInactiveModal
        open={isMarkInactiveModalOpen}
        user={user}
        onClose={handleMarkInactiveModalClose}
        calendarDate={date}
        onRefresh={onRefresh}
      />
      <RemoveInactiveModal
        open={isRemoveInactiveModalOpen}
        user={user}
        onClose={handleRemoveInactiveModalClose}
        onRefresh={onRefresh}
      />
      {isEditCapacityModalOpen && (
        <EditCapacityModal
          open={isEditCapacityModalOpen}
          user={user}
          onClose={handleEditCapacityModalClose}
          calendarDate={date}
          onRefresh={onRefresh}
        />
      )}
    </Box>
  );
}

const useStyles = makeStyles()(
  (theme, { color, isSelected, isSelectable } = {}) => {
    const backgroundColor = isSelected
      ? theme.palette.orange.light
      : theme.palette.common.white;

    return {
      container: {
        boxSizing: "border-box",
        width: COLUMN_WIDTH,
        padding: theme.spacing(0.5),
        borderRadius: 4,
        display: "flex",
        flexDirection: "column",
        ...(isSelectable && { cursor: "pointer" }),
      },
      statusContainer: {
        backgroundColor,
        padding: theme.spacing(0, 0.5),
        display: "flex",
        gap: theme.spacing(2),
        cursor: "pointer",
      },
      icon: {
        display: "flex",
        alignItems: "center",
      },
      header: {
        backgroundColor,
        borderRadius: theme.spacing(2, 2, 0, 0),
        padding: theme.spacing(0, 1),
        display: "flex",
        gap: theme.spacing(2),
        transition: "background-color 0.1s",
      },
      text: {
        whiteSpace: "nowrap",
        overflow: "hidden",
        width: "100%",
        fontSize: theme.fontSize.small,
      },
      avatar: {
        width: 16,
        height: 16,
        fontSize: theme.fontSize.xxsmall,
      },
      borderColor: { border: `2px solid ${color}`, maxWidth: "100%" },
      flexCenter: {
        display: "flex",
        alignItems: "center",
      },
    };
  }
);
